import React, { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import { AuthProvider } from "./hooks/use-auth";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { Login } from "./pages/Login";
import { ThemeProvider } from "@mui/material";
import { msalInstance } from "./services/msalInstance";
import { useAuth } from "./auth/useAuth";
import { ToastProvider } from "./context/ToastContext";
import NotFound from "./pages/NotFound";
import { loginUrl, budgetModuleUrl, liquidityModuleUrl, dataMappingModuleUrl, balanceSheetModuleUrl, profitAndLossModuleUrl } from './routes/urls';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import BudgetModule from "./pages/BudgetModule";
import LiquidityModule from "./pages/LiquidityModule";
import DataMappingModule from "./pages/DataMappingModule";
import { theme } from "./theme/theme";
import BalanceSheetModule from "./pages/BalanceSheetModule";
import ProfitAndLossModule from "./pages/ProfitAndLossModule";
import { CurrencyProvider } from "./context/CurrencyContext";

const AuthenticatedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isInitialized, isLoading } = useAuth();
  const location = useLocation();

  if (!isInitialized || isLoading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? (
    <>{children}</>
  ) : (
    <Navigate to={loginUrl} state={{ from: location }} />
  );
};

const AppContent = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const url = new URL(window.location.href);
      url.hash = "";
      window.history.replaceState({}, document.title, url.toString());
    }
  }, [location]);

  return (
    <Routes>
      <Route
        path={budgetModuleUrl}
        element={
          <AuthenticatedRoute>
            <BudgetModule />
          </AuthenticatedRoute>
        }
      />
      <Route
        path={liquidityModuleUrl}
        element={
          <AuthenticatedRoute>
            <LiquidityModule />
          </AuthenticatedRoute>
        }
      />
      <Route
        path={balanceSheetModuleUrl}
        element={
          <AuthenticatedRoute>
            <BalanceSheetModule />
          </AuthenticatedRoute>
        }
      />
      <Route
        path={dataMappingModuleUrl}
        element={
          <AuthenticatedRoute>
            <DataMappingModule />
          </AuthenticatedRoute>
        }
      />
      <Route
        path={profitAndLossModuleUrl}
        element={
          <AuthenticatedRoute>
            <ProfitAndLossModule />
          </AuthenticatedRoute>
        }
      />
      <Route path={loginUrl} element={<Login />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

const App = () => (
  <BrowserRouter>
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <CurrencyProvider>
                <ToastProvider>
                  <AppContent />
                </ToastProvider>
              </CurrencyProvider>
            </LocalizationProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </AuthProvider>
    </MsalProvider>
  </BrowserRouter>
);

export default App;